import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import { LineItemsEditor, emptyLine, type LineDraft } from '../components/LineItemsEditor';
import {
  useClients,
  useCreateInvoice,
  useInvoiceAction,
  useInvoices,
  useProjects,
} from '../hooks/useOps';
import { opsApi } from '../api/client';
import type { Invoice } from '../api/types';
import { formatDate, formatIDR, humanizeStatus, statusColor } from '../lib/format';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function plusDaysISO(days: number) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function InvoicesPage() {
  const { data, isLoading, error } = useInvoices();
  const { data: clients } = useClients();
  const { data: projects } = useProjects();
  const createInvoice = useCreateInvoice();
  const action = useInvoiceAction();

  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [milestoneIds, setMilestoneIds] = useState<string[]>([]);
  const [issueDate, setIssueDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(plusDaysISO(30));
  const [withholds, setWithholds] = useState(true);
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [formError, setFormError] = useState<string | null>(null);

  // Record-payment dialog
  const [payFor, setPayFor] = useState<Invoice | null>(null);
  const [payAmount, setPayAmount] = useState(0);
  const [payError, setPayError] = useState<string | null>(null);

  const clientName = useMemo(() => {
    const map = new Map((clients ?? []).map((c) => [c.id, c.name]));
    return (id: string) => map.get(id) ?? id.slice(0, 8);
  }, [clients]);

  const selectedProject = (projects ?? []).find((p) => p.id === projectId) ?? null;
  // Billing milestones this invoice can claim: ready and not already on an invoice.
  const claimable = (selectedProject?.milestones ?? []).filter(
    (m) => m.status === 'ready_to_invoice' && !m.invoiceId,
  );
  const usingMilestones = milestoneIds.length > 0;

  const openCreate = () => {
    setClientId('');
    setProjectId('');
    setMilestoneIds([]);
    setIssueDate(todayISO());
    setDueDate(plusDaysISO(30));
    setWithholds(true);
    setLines([emptyLine()]);
    setFormError(null);
    setOpen(true);
  };

  const pickProject = (id: string) => {
    setProjectId(id);
    setMilestoneIds([]);
    // Billing goes to the project's client; prefill so the two can't diverge.
    const proj = (projects ?? []).find((p) => p.id === id);
    if (proj) setClientId(proj.clientId);
  };

  const submit = async () => {
    try {
      await createInvoice.mutateAsync({
        clientId,
        projectId: projectId || undefined,
        milestoneIds: usingMilestones ? milestoneIds : undefined,
        issueDate,
        dueDate,
        clientWithholdsPph: withholds,
        // With milestones selected, the backend derives one line per milestone.
        lines: usingMilestones
          ? undefined
          : lines.map((l) => ({
              description: l.description,
              quantity: l.quantity,
              unitPrice: l.unitPrice,
            })),
      });
      setOpen(false);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const doAction = async (id: string, act: 'issue') => {
    try {
      await action.mutateAsync({ id, action: act });
    } catch (e) {
      window.alert((e as Error).message);
    }
  };

  const downloadPdf = async (inv: Invoice) => {
    try {
      const blob = await opsApi.invoicePdf(inv.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoiceNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert((e as Error).message);
    }
  };

  const submitPayment = async () => {
    if (!payFor) return;
    try {
      await action.mutateAsync({ id: payFor.id, action: 'pay', body: { amount: payAmount } });
      setPayFor(null);
    } catch (e) {
      setPayError((e as Error).message);
    }
  };

  const columns: GridColDef<Invoice>[] = [
    { field: 'invoiceNumber', headerName: 'Invoice', minWidth: 150 },
    {
      field: 'clientId',
      headerName: 'Client',
      flex: 1,
      minWidth: 160,
      valueGetter: (v) => clientName(v as string),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (p) => (
        <Chip
          size="small"
          label={humanizeStatus(p.value)}
          color={statusColor(p.value)}
          variant="outlined"
        />
      ),
    },
    { field: 'dueDate', headerName: 'Due', width: 120, valueGetter: (v) => formatDate(v as string) },
    {
      field: 'total',
      headerName: 'Total',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (v) => formatIDR(v as string),
    },
    {
      field: 'amountPaid',
      headerName: 'Paid',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (v) => formatIDR(v as string),
    },
    {
      field: 'actions',
      headerName: '',
      width: 250,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" gap={1}>
          {p.row.status === 'draft' && (
            <Button size="small" onClick={() => doAction(p.row.id, 'issue')}>
              Issue
            </Button>
          )}
          {['issued', 'sent', 'partially_paid', 'overdue'].includes(p.row.status) && (
            <Button
              size="small"
              onClick={() => {
                setPayFor(p.row);
                setPayAmount(0);
                setPayError(null);
              }}
            >
              Record payment
            </Button>
          )}
          <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => downloadPdf(p.row)}>
            PDF
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Invoices"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New invoice
          </Button>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ height: 600 }}>
        <DataGrid
          rows={data ?? []}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>

      {/* Create invoice */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>New invoice</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <Stack direction="row" gap={2}>
              <TextField
                select
                label="Project (optional)"
                value={projectId}
                onChange={(e) => pickProject(e.target.value)}
                sx={{ flex: 1 }}
              >
                <MenuItem value="">
                  <em>None — free-form invoice</em>
                </MenuItem>
                {(projects ?? []).map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.code} — {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Client"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                sx={{ flex: 1 }}
                required
                disabled={Boolean(selectedProject)}
              >
                {(clients ?? []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            {selectedProject && (
              <TextField
                select
                label="Bill milestones (ready to invoice)"
                value={milestoneIds}
                onChange={(e) =>
                  setMilestoneIds(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                  )
                }
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    claimable
                      .filter((m) => (selected as string[]).includes(m.id))
                      .map((m) => m.name)
                      .join(', '),
                }}
                helperText={
                  claimable.length === 0
                    ? 'No ready_to_invoice milestones on this project — add lines manually below.'
                    : 'One invoice line per milestone; issuing flips them to invoiced.'
                }
              >
                {claimable.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    <Checkbox checked={milestoneIds.includes(m.id)} size="small" />
                    <ListItemText primary={m.name} secondary={formatIDR(m.amount)} />
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Stack direction="row" gap={2}>
              <TextField
                label="Issue date"
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Due date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch checked={withholds} onChange={(e) => setWithholds(e.target.checked)} />
              }
              label="Client withholds PPh (0.5% final — Suket PP 55)"
            />
            {usingMilestones ? (
              <Typography variant="body2" color="text.secondary">
                Lines derive from the selected milestones —{' '}
                {formatIDR(
                  claimable
                    .filter((m) => milestoneIds.includes(m.id))
                    .reduce((sum, m) => sum + Number(m.amount), 0),
                )}{' '}
                subtotal.
              </Typography>
            ) : (
              <LineItemsEditor lines={lines} onChange={setLines} />
            )}
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={
              !clientId ||
              (!usingMilestones && lines.some((l) => !l.description)) ||
              createInvoice.isPending
            }
          >
            Create draft
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record payment */}
      <Dialog open={Boolean(payFor)} onClose={() => setPayFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Record payment</DialogTitle>
        <DialogContent>
          {payFor && (
            <Stack gap={2} mt={1}>
              <Alert severity="info">
                {payFor.invoiceNumber} — total {formatIDR(payFor.total)}, already paid{' '}
                {formatIDR(payFor.amountPaid)}.
                {payFor.clientWithholdsPph &&
                  ` Expect ${formatIDR(payFor.pphAmount)} withheld as PPh.`}
              </Alert>
              <TextField
                label="Amount received"
                type="number"
                value={payAmount}
                onChange={(e) => setPayAmount(Number(e.target.value))}
                autoFocus
              />
              {payError && <Alert severity="error">{payError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayFor(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitPayment}
            disabled={payAmount <= 0 || action.isPending}
          >
            Record
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
